import { createSelector } from '@reduxjs/toolkit';
import {
  BookingObject,
  Desk,
  PlaceType,
  TimeBounds,
  TimePeriod,
} from 'src/types';
import { getDateTime } from 'src/utils/getDateTime';
import { validateDate } from 'src/utils/validations/validateDate';

import { StoreState } from '../types';

import { defaultDeskBookingFilters } from './initialState';
import { DesksState } from './types';

const maxDurationInDays = 14;
export const deskBookingDurationHours = 14;

export const getDesksState = (state: StoreState): DesksState => state.desks;

export const getDeskLocation = createSelector(getDesksState, desks => ({
  loading: desks.locationLoading,
  error: desks.locationError,
  town: desks.town,
  building: desks.building,
  floor: desks.floor,
}));

export const getDesks = (state: StoreState): DesksState['bookingObjects'] =>
  state.desks.bookingObjects;

export const getDesksTown = (state: StoreState): DesksState['town'] =>
  state.desks.town;

export const getDesksDate = (state: StoreState): DesksState['date'] =>
  state.desks.date;

export const getDesksFilters = (state: StoreState): DesksState['filters'] =>
  state.desks.filters;

export const getDesksTimePeriodUTC = createSelector(
  getDesksDate,
  getDesksTown,
  (date, town) => {
    const result: TimePeriod = {
      from: undefined,
      to: undefined,
    };

    if (!date) return result;

    const fromDate = getDateTime({
      date: new Date(date),
      timezoneOffset: town?.timeZone,
      hours: 7,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
      toUTC: true,
    });

    const toDate = new Date(fromDate);
    toDate.setHours(toDate.getHours() + deskBookingDurationHours);

    result.from = fromDate.toISOString();
    result.to = toDate.toISOString();

    return result;
  },
);

export const getDesksTimeBounds = createSelector(
  getDesksDate,
  getDesksTown,
  (date, town) => {
    const result: TimeBounds = {
      min: undefined,
      max: undefined,
      error: undefined,
    };

    if (!date) return result;

    const min = getDateTime({
      timezoneOffset: town?.timeZone,
    });
    const max = getDateTime({
      date: min,
      hours: 23,
      minutes: 59,
      seconds: 59,
      milliseconds: 999,
      timezoneOffset: town?.timeZone,
    });
    min.setDate(min.getDate() - 1);
    max.setDate(max.getDate() + maxDurationInDays);
    const error = validateDate(date, min, max);

    result.min = min.toISOString();
    result.max = max.toISOString();
    result.error = error;

    return result;
  },
);

export const getFilteredDesks = createSelector(
  getDesks,
  getDesksFilters,
  (desks, filters) => {
    if (!desks.size) return desks;

    if (filters === defaultDeskBookingFilters) return desks;

    const filtered = new Map<string, BookingObject>();

    for (const [key, value] of desks) {
      if (value.type !== PlaceType.Desk) continue;

      const filteredByWindow = filters.byWindow ? value.byWindow : true;
      const filteredByMonitor = filters.withMonitor ? value.withMonitor : true;
      const filteredByCharger = filters.withCharger ? value.withCharger : true;
      const filteredByDivider = filters.withDivider ? value.withDivider : true;

      if (
        filteredByWindow &&
        filteredByMonitor &&
        filteredByCharger &&
        filteredByDivider
      )
        filtered.set(key, value);
    }

    return filtered;
  },
);

export const getDesksFiltersCount = createSelector(getDesksFilters, filters => {
  return (
    +(filters.byWindow !== defaultDeskBookingFilters.byWindow) +
    +(filters.withMonitor !== defaultDeskBookingFilters.withMonitor) +
    +(filters.withCharger !== defaultDeskBookingFilters.withCharger) +
    +(filters.withDivider !== defaultDeskBookingFilters.withDivider)
  );
});

export const getCurrentDesk = createSelector(getDesksState, desks =>
  desks.deskId
    ? ((desks.bookingObjects.get(desks.deskId) as Desk) ?? undefined)
    : undefined,
);

export const getDesksExcludedDates = createSelector(
  getDesksState,
  desks => desks.excludedDates,
);
